import { makeStyles } from '@material-ui/styles'

import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import Badge from 'core/elements/badge'
import Button from 'core/elements/button/Button'
import IconButton from 'core/elements/button/IconButton'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import React from 'react'
import useReactRouter from 'use-react-router'
import { IAppsDetailsPageTabs } from '../../model'
import CreateNewAppModal from '../app/CreateNewAppModal'
import { defaultAppIconPath } from '../constant'
import { appStatus, getResourceDetails, useBreakpointMatch } from '../helper'
import InfoBox from '../InfoBox'
import RowInfo from '../RowInfo'

const useStyles = makeStyles<Theme, { showWarningCount: boolean }>((theme) => ({
  row: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 24px',
    gap: '12px',
    background: theme.components.card.background,
    border: `1px solid ${colors.grey[200]}`,
    borderRadius: 4,
    position: 'relative',
  },
  headerContent: {
    width: '100%',
  },
  headerText: {
    display: 'grid',
    gap: theme.spacing(0.5),
    minWidth: 180,
  },
  subContainer: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
  },
  checkContainer: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  content: {
    display: 'grid',
  },
  iconsContainer: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    minWidth: 220,
  },
  resourceContainer: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 320,
  },
  statusContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  badgeContainer: {
    display: 'flex',
    flexWrap: 'wrap', // Allow badges to wrap
    gap: theme.spacing(1),
    alignItems: 'center',
    [theme.breakpoints.down(1600)]: {
      maxWidth: 120, // Ensure it doesn’t overflow the container
      minWidth: 120,
    },
  },
  divider: {
    width: 1,
    height: 20,
    background: colors.grey[200],
  },
  iconWrapper: {
    width: 56,
    height: 56,
  },
  customButton: {
    minWidth: '100%',
    '& .button-text': {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      gap: theme.spacing(1),
    },
  },
  actionContainer: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    '& > div:first-of-type': {
      display: 'flex',
      gap: 24,
      justifyContent: 'normal',
    },
  },
  settingsMenuButton: {
    '&:hover': {
      backgroundColor: colors.grey[200],
    },
  },
  badge: {
    backgroundColor: '#FAFAFC',
    borderColor: '1px solid #E6E6EA',
  },
  icon: {
    minWidth: 50,
  },
}))

const TemplateListView = ({
  iconName,
  headerName,
  subHeaderName,
  vms,
  status,
  version,
  ownerName,
  handleClick,
  data,
}) => {
  const showWarningCount = false
  const { history } = useReactRouter()
  const isBelow1800 = useBreakpointMatch(1800)

  const classes = useStyles({ showWarningCount })

  const isBelowCustom = useBreakpointMatch(1600)

  const resourceDetails = getResourceDetails(data, true)

  const templateDetails = [
    {
      label: 'Owner',
      value: ownerName,
      imagePath: '',
      showDivider: false,
    },
  ]

  const redirectToCreateVolume = (selectedTemplateId) => {
    history.replace(
      `${routeManager.routes.openstack.createApps.path({
        tab: IAppsDetailsPageTabs.Allapps,
      })}?selectedTemplateId=${selectedTemplateId}`,
    )
  }
  const onButtonClick = () => {
    redirectToCreateVolume(data?.uid)
  }

  return (
    <>
      <CreateNewAppModal
        addRoute={routeManager.routes.openstack.createApps}
        tabName={IAppsDetailsPageTabs.Templates}
        data={data}
      />
      <div className={classes.container}>
        <div className={classes.headerContent}>
          <div className={classes.row}>
            <div className={classes.iconsContainer}>
              <div className={classes.icon}>
                <img src={iconName || defaultAppIconPath} className={classes.iconWrapper} />
              </div>

              <div className={classes.headerText}>
                <Text variant="subtitle2">{headerName}</Text>
                <Text variant="body2">{subHeaderName}</Text>
              </div>
              <div className={classes.badgeContainer}>
                <Badge className={classes.badge} variant={appStatus.Default} text={`${vms} VMs`} />
                <Badge className={classes.badge} variant={appStatus.Default} text={version} />
              </div>
            </div>
            {/*  */}
            <div className={classes.resourceContainer}>
              {resourceDetails?.map((item, index) => (
                <InfoBox
                  label={item?.label}
                  value={item?.value}
                  imagePath={item?.imagePath}
                  showDivider={item?.showDivider}
                  isCompact={true}
                />
              ))}

              {isBelow1800 ? (
                <div className={classes.headerText}>
                  {templateDetails?.map((item, index) => (
                    <RowInfo
                      label={item?.label}
                      value={item?.value}
                      minWidth={60}
                      isCompact={isBelowCustom}
                    />
                  ))}
                </div>
              ) : (
                templateDetails?.map((item, index) => (
                  <InfoBox
                    label={item?.label}
                    value={item?.value}
                    imagePath={item?.imagePath}
                    showDivider={item?.showDivider}
                  />
                ))
              )}
            </div>
            <div className={classes.actionContainer}>
              <div>
                <Button
                  variant="secondary"
                  // disabled={params.terraformModulePath !== '' && params.tag !== ''}
                  onClick={onButtonClick}
                  icon="plus"
                >
                  Create APP
                </Button>
              </div>
              {isAdmin() && (
                <IconButton
                  size="md"
                  icon="ellipsis-vertical"
                  solid={true}
                  className={classes.settingsMenuButton}
                  onClick={handleClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default TemplateListView
